/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from 'react';
import { Input, Toggle } from '@skyline-platform/alchemy';
import PropTypes from 'prop-types';
import './StreamForm.css';
import { validateStreamName, validateStreamDescription } from '@utils/formValidators';
import { SearchExistingZone } from '@components/FormComponents/ZoneForm/SearchExistingZone';
import { SearchExistingAppliance } from '@components/FormComponents/ApplianceForm/SearchExistingAppliance';
import * as is from 'is';
import { getStreams } from '@utils/graphql/creators/streams';
import { useDebouncedCallback } from 'use-debounce';
import { getLogger } from '@utils/general';
const logger = getLogger('src.components.StreamsForm');

export const StreamForm = ({ formData, setFormData }) => {
  const [name, setName] = useState(formData.stream.name || '');
  const [description, setDescription] = useState(formData.stream.description || '');
  const [onDemand, setOnDemand] = useState(formData.stream.onDemand ?? true);
  const [rtspInterleaved, setRtspInterleaved] = useState(formData.stream.rtspInterleaved ?? false);
  // snapshotInterval is written as camelCase by this form, but the API returns it as
  // snapshot_interval — check both so the value survives remounts (e.g. navigating back in the stepper)
  const [snapshotInterval, setSnapshotInterval] = useState(
    formData.stream.snapshotInterval ?? formData.stream.snapshot_interval ?? ''
  );
  const [error, setError] = useState({ name: false, description: false });
  const [duplicateTypeInfo, setDuplicateTypeInfo] = useState({});
  const [zoneId, setZoneId] = useState(formData.zone.id);
  const [applianceId, setApplianceId] = useState(formData.appliance.id);

  // Validate input fields
  const validateInputs = () => {
    const nameValid = !is.empty(duplicateTypeInfo)
      ? {
        isValid: false,
        error: (
          <>
            {duplicateTypeInfo.zoneId && (
              <p>
                Stream name already exists in this zone. Please
                update the stream name or select another zone.
              </p>
            )}
            {duplicateTypeInfo.applianceId && (
              <p>
                Stream name already exists in this host appliance.
                Please update the stream name or select another appliance.
              </p>
            )}
          </>
        ),
      }
      : validateStreamName(name);

    const descValid = validateStreamDescription(description).isValid;
    const snapshotIntervalValid = snapshotInterval === '' || snapshotInterval >= 15;

    setError({
      name: nameValid,
      description: !descValid,
      snapshotInterval: !snapshotIntervalValid,
    });

    return nameValid.isValid && descValid && snapshotIntervalValid;
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing pattern
    const inputFieldsValid = validateInputs();
    const zoneValid = formData.zone && !is.empty(formData.zone.id);
    const applianceValid = formData.appliance && !is.empty(formData.appliance.id);

    setFormData((prevData) => ({
      ...prevData,
      stream: {
        ...prevData.stream,
        name,
        description,
        onDemand,
        rtspInterleaved,
        snapshotInterval,
      },
      isValid: inputFieldsValid && zoneValid && applianceValid,
    }));
  }, [
    name,
    description,
    onDemand,
    rtspInterleaved,
    snapshotInterval,
    formData.zone,
    formData.appliance,
    duplicateTypeInfo,
  ]);

  const checkStreamNameDuplicate = async (name, zoneId, applianceId, setDuplicateTypeInfo) => {
    let duplicateInfo = {};

    const checkDuplicate = async (id, type) => {
      if (!id || name === '') return;

      const { query, execute } = getStreams(null, { name, [type]: id });
      query.addReturnProperties('success', 'error', 'id', 'name');

      try {
        const { error, data } = await execute();
        if (error) {
          logger.error(error);
          return;
        }
        /**
         * The third part of the if statement is needed so the duplicate error message doesn't show
         * when the user initially edits a stream, but will show if the user changes the
         * stream name to a DIFFERENT stream that already exists
         */
        if (data && data.length > 0 && formData.stream.id !== data[0].id) {
          duplicateInfo[type] = id;
        }
      } catch {
        logger.error(
          `There was a failure retrieving stream data for stream ${name} associated with ${type}: ${id}`
        );
      }
    };

    if (zoneId) {
      await checkDuplicate(zoneId, 'zoneId');
    }

    if (applianceId) {
      await checkDuplicate(applianceId, 'applianceId');
    }
    setDuplicateTypeInfo(duplicateInfo);
  };

  const debouncedCheckStreamNameDuplicate = useDebouncedCallback(checkStreamNameDuplicate, 300);

  useEffect(() => {
    debouncedCheckStreamNameDuplicate(name, zoneId, applianceId, setDuplicateTypeInfo);
  }, [
    name,
    zoneId,
    applianceId,
  ]);

  return (
    <gar-add-new-stream>
      <Input
        id='name'
        inputLabel='Name'
        helperText='Name of the Stream'
        required
        key='1'
        error={error.name.isValid}
        value={name}
        errorMessage={error.name.error}
        onChange={(e) => setName(e.target.value)}
      />
      <Input
        id='description'
        inputLabel='Description'
        helperText='Description of the Stream'
        key='2'
        error={error.description}
        value={description}
        errorMessage={error.description ? 'Description exceeded maximum length of 140.' : ''}
        onChange={(e) => setDescription(e.target.value)}
        multiline
        rows={4}
      />
      <div className='toggle-wrapper'>
        <Toggle
          label='On Demand'
          checked={onDemand}
          handleChange={(e) => setOnDemand(e.target.checked)}
        />
        <Toggle label='RTSP Interleaved'
          checked={rtspInterleaved}
          handleChange={(e) => setRtspInterleaved(e.target.checked)} />
      </div>
      <Input
        id='snapshotInterval'
        inputLabel='Snapshot Interval (seconds)'
        key='3'
        error={error.snapshotInterval}
        value={snapshotInterval}
        errorMessage={error.snapshotInterval ? 'Snapshot Interval must be greater than or equal to 15.' : ''}
        onChange={(e) => setSnapshotInterval(e.target.value)}
        type='number'
        slotProps={{
          input: {
            // min snapshot interval is 15 seconds
            min: 15,
          },
        }}
        helperText='Leaving this field blank will default to the global snapshot interval value.'
      />
      <SearchExistingZone
        formData={formData.zone}
        setFormData={(zoneData) => {
          setZoneId(zoneData.id);
          setFormData((prevData) => ({
            ...prevData,
            zone: zoneData,
          }));
        }}
      />
      <SearchExistingAppliance
        formData={formData.appliance}
        fieldName='Host Appliance'
        setFormData={(applianceData) => {
          setApplianceId(applianceData.id);
          setFormData((prevData) => ({
            ...prevData,
            appliance: applianceData,
          }));
        }}
      />
    </gar-add-new-stream>
  );
};

StreamForm.propTypes = {
  formData: PropTypes.shape({
    stream: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      description: PropTypes.string,
      onDemand: PropTypes.bool,
      rtspInterleaved: PropTypes.bool,
      snapshot_interval: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      snapshotInterval: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }).isRequired,
    zone: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }).isRequired,
    appliance: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }).isRequired,
  }).isRequired,
  setFormData: PropTypes.func.isRequired,
};